import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Boxes, Loader2, MailCheck, Eye, EyeOff } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";

const BG = "https://images.unsplash.com/photo-1688494930045-328d0f95efe9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwxfHxkYXJrJTIwYWJzdHJhY3QlMjBnZW9tZXRyaWMlMjBtaW5pbWFsaXN0JTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3ODI0ODY1NjZ8MA&ixlib=rb-4.1.0&q=85";

export default function Register() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [shaking, setShaking] = useState(false);
  const [hasShaken, setHasShaken] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [resent, setResent] = useState(false);
  const [showPw, setShowPw] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/register", { name, email, password });
      setRegistered(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setShaking(true);
      setHasShaken(true);
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    try {
      await api.post("/auth/resend-verification", { email });
      setResent(true);
    } catch {}
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center bg-[#0A0A0B] text-white p-6">
      <img src={BG} alt="" className="absolute inset-0 h-full w-full object-cover opacity-40" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0B]/70 via-[#0A0A0B]/80 to-[#0A0A0B]" />
      <div
        data-testid="register-card"
        onAnimationEnd={() => setShaking(false)}
        className={`relative w-full max-w-sm bg-[#121214]/90 backdrop-blur-xl border border-white/10 rounded-2xl p-8 shadow-[0_30px_80px_rgba(0,0,0,0.6)] ${shaking ? "animate-shake" : hasShaken ? "" : "animate-fade-up"}`}
      >
        <div className="flex items-center justify-center gap-2 mb-8">
          <div className="h-9 w-9 rounded-lg bg-primary flex items-center justify-center">
            <Boxes className="h-5 w-5" />
          </div>
          <span className="font-display font-extrabold text-xl">Portal GA</span>
        </div>
        <h1 className="font-display text-3xl font-bold mb-1 text-center">{registered ? "Verifica tu correo" : "Crear cuenta"}</h1>
        {registered ? (
          <div className="text-center" data-testid="register-check-email">
            <div className="h-14 w-14 rounded-full bg-[#00E676]/15 flex items-center justify-center mx-auto my-6 animate-pop">
              <MailCheck className="h-7 w-7 text-[#00E676]" />
            </div>
            <p className="text-sm text-zinc-400 mb-2">Te enviamos un enlace de confirmación a</p>
            <p className="text-sm font-semibold text-white mb-4" data-testid="register-sent-email">{email}</p>
            <p className="text-xs text-zinc-500 mb-6">Haz clic en el enlace del correo para activar tu cuenta. Revisa también la carpeta de spam.</p>
            <button onClick={() => navigate("/login")} data-testid="register-goto-login-btn"
              className="w-full bg-primary hover:bg-[#FF5047] text-white font-semibold py-2.5 rounded-lg transition-colors mb-3">
              Ir a iniciar sesión
            </button>
            <button onClick={resend} disabled={resent} data-testid="register-resend-btn"
              className="w-full bg-white/5 hover:bg-white/10 border border-white/10 text-zinc-300 font-semibold py-2.5 rounded-lg transition-colors text-sm disabled:opacity-60">
              {resent ? "Correo reenviado ✓" : "Reenviar correo de verificación"}
            </button>
          </div>
        ) : (
        <>
        <p className="text-zinc-500 mb-8 text-sm text-center">Solo correos <span className="text-primary">@galletasaromaticas.com</span></p>
        <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Nombre</label>
              <input
                data-testid="register-name"
                required value={name} onChange={(e) => setName(e.target.value)} placeholder="Tu nombre"
                className="mt-1.5 w-full bg-[#121214] border border-white/10 rounded-lg px-3 py-2.5 text-sm placeholder:text-zinc-600 focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-colors"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Correo</label>
              <input
                data-testid="register-email"
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@galletasaromaticas.com"
                className="mt-1.5 w-full bg-[#121214] border border-white/10 rounded-lg px-3 py-2.5 text-sm placeholder:text-zinc-600 focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-colors"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Contraseña</label>
              <div className="relative mt-1.5">
                <input
                  data-testid="register-password"
                  type={showPw ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="Mínimo 6 caracteres"
                  className="w-full bg-[#121214] border border-white/10 rounded-lg px-3 py-2.5 pr-10 text-sm placeholder:text-zinc-600 focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-colors"
                />
                <button type="button" onClick={() => setShowPw((v) => !v)} data-testid="register-toggle-password" tabIndex={-1}
                  aria-label={showPw ? "Ocultar contraseña" : "Mostrar contraseña"}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white transition-colors">
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            {error && <p data-testid="register-error" className="text-sm text-red-400 bg-red-500/10 rounded-lg px-3 py-2">{error}</p>}
            <button
              data-testid="register-submit"
              type="submit" disabled={loading}
              className="w-full bg-primary hover:bg-[#FF5047] text-white font-semibold py-2.5 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              Crear cuenta
            </button>
          </form>
          <p className="text-sm text-zinc-500 mt-6 text-center">
            ¿Ya tienes cuenta?{" "}
            <Link to="/login" data-testid="goto-login" className="text-primary font-semibold hover:underline">Inicia sesión</Link>
          </p>
        </>
        )}
      </div>
    </div>
  );
}
